import { listsApi } from '../../api/lists-api';
import type { IListCriteriaGroup, IListCountKey, ListCriteriaNode } from '../../api/lists-contracts';
import { useAdminData } from '../../components/admin-page';
import { signalsOf, type CardVariant, type IStateSignal, type SignalSpec } from '../../components/state-cards';
import { CYCLE_OPEN } from '../../lists';
import type { IWorkSignal } from '../lists/work-page/signals';

/**
 * The reason cards' live signals (SR-456): survey-api's `/lists/counts`, one read under the
 * worklist's filters but the signals pressed — each card's criteria with one more condition, keyed
 * `<card>.<signal>`, every signal of every card read whatever its handling, the handling choosing
 * which the card draws.
 */

const LAST_EDIT = 'sys.advisor_last_edit_at';

/** The note wordings that keep an advisor out of the next ranking too — the page's `SIT_OUT`, as
 *  `contains` leaves (survey-api matches them ignoring case), over the whole note: the criteria
 *  cannot say "since the reason was set", which the Exceptions group reads, so the signal is named
 *  for what it counts. */
const SIT_OUT_PHRASES = ['sit out', 'next ranking', 'following ranking', 'two years'];

type Kind = 'edited' | 'no_outreach' | 'sit_out';

/** A reason's signals by its handling, the one a compact card shows first (none where empty) and
 *  those `on`. */
function kindsOf(handling: Nullable<string>, variant: CardVariant, on: (key: string) => boolean): Kind[] {
  const decided = handling === 'edit_vs_outreach' || handling === 'firm_compare';
  const every: Kind[] = [...(handling === 'bulk_except_noted' ? (['sit_out'] as const) : []), 'edited', 'no_outreach'];
  if (variant === 'detailed') return every;
  const first: Kind | null = decided ? 'edited' : handling === 'bulk_except_noted' ? 'sit_out' : null;
  return every.filter((kind) => kind === first || on(kind));
}

/** A card's count key by its place: a reason's code is the data's own — "Left Firm",
 *  "Advisor doesn’t want…" — and survey-api takes a key of letters, digits and `_ . : -` alone. */
const keyOf = (at: number) => `card${at}`;

/** The signal lines a reason's card draws, which it holds room for while they are read. */
export function signalLines(
  handling: Nullable<string>,
  variant: CardVariant,
  outreach: Nullable<string>,
  notes: Nullable<string>
): number {
  const signals = reasonSignals(outreach, notes);
  return kindsOf(handling, variant, () => false).filter((kind) => signals[kind]).length;
}

/** The reason cards' signals — words and conditions — by kind; one whose field the survey lacks
 *  is none. Each is also a filter of the page (`work-page/signals.ts`). */
export function reasonSignals(
  outreach: Nullable<string>,
  notes: Nullable<string>
): Record<Kind, Nullable<SignalSpec & IWorkSignal & { leaves: ListCriteriaNode[] }>> {
  return {
    edited: {
      key: 'edited',
      label: 'edited this cycle',
      words: 'edited this cycle',
      tone: 'success',
      leaves: [{ field: LAST_EDIT, op: 'since', value: CYCLE_OPEN }]
    },
    no_outreach: outreach
      ? {
          key: 'no_outreach',
          label: 'no outreach',
          words: 'no outreach',
          tone: 'attention',
          leaves: [{ field: outreach, op: 'empty' }]
        }
      : null,
    sit_out: notes
      ? {
          key: 'sit_out',
          label: 'sit-out phrase in notes',
          words: 'sit-out phrase in notes',
          tone: 'attention',
          leaves: [{ op: 'or', children: SIT_OUT_PHRASES.map((value) => ({ field: notes, op: 'contains', value })) }]
        }
      : null
  };
}

/** The cards' signals by card key, null until read. `criteriaFor` is a card's own criteria with
 *  `extra` ANDed on, the filters apart; `scope` the worklist's rows, which the read narrows to
 *  before it counts; `handlings` each reason's handling (the legacy card has none); `on` says a
 *  card's signal pressed, which a compact card shows too. */
export function useReasonSignals({
  survey,
  cards,
  filters,
  scope,
  handlings,
  criteriaFor,
  outreach,
  notes,
  variant,
  counted,
  on
}: {
  survey: string;
  cards: readonly string[];
  filters: readonly ListCriteriaNode[];
  scope: Nullable<ListCriteriaNode>;
  handlings: Nullable<ReadonlyMap<string, string>>;
  criteriaFor: (card: string, extra: readonly ListCriteriaNode[]) => IListCriteriaGroup;
  outreach: Nullable<string>;
  notes: Nullable<string>;
  variant: CardVariant;
  counted: string;
  on: (card: string, key: string) => boolean;
}): Nullable<Record<string, IStateSignal[]>> {
  const signals = reasonSignals(outreach, notes);
  const asked = JSON.stringify([counted, cards, outreach, notes, scope]);
  const read = useAdminData<Nullable<{ asked: string; counts: Record<string, number> }>>(
    async (signal) => {
      if (cards.length === 0) return { asked, counts: {} };
      const keys: IListCountKey[] = cards.flatMap((card, at) =>
        Object.values(signals).flatMap((one) =>
          one ? [{ key: `${keyOf(at)}.${one.key}`, criteria: criteriaFor(card, one.leaves) }] : []
        )
      );
      // The worklist's rows first: each key's criteria repeat them, but the read then counts a
      // few thousand rows, not the survey's every row.
      const children = scope ? [...filters, scope] : [...filters];
      const query = { survey, criteria: { version: 1, op: 'and', children } };
      // survey-api counts at most 60 keys a read: a worklist with more reasons draws the first's.
      return { asked, counts: (await listsApi.counts({ query, counts: keys.slice(0, 60) }, signal)).counts };
    },
    [asked]
  );
  // A failed read draws the cards without signals rather than holding their lines for good.
  if (read.data?.asked !== asked) return read.error ? {} : null;
  const { counts } = read.data;
  return Object.fromEntries(
    cards.map((card, at) => [
      card,
      signalsOf(
        counts,
        `${keyOf(at)}.`,
        kindsOf(handlings?.get(card) ?? null, variant, (key) => on(card, key)).flatMap((kind) => signals[kind] ?? [])
      )
    ])
  );
}
